import type MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { downloadFile } from "@core-ui/downloadResource";
import useChatStore from "@ext/agent/components/store/ChatStore";
import { fetchStoredAttachment } from "@ext/agent/components/utils/fetchStoredAttachment";
import type { ChatAttachment } from "@ext/agent/core/attachmentStore";
import t from "@ext/localization/locale/translate";
import { toast } from "@ui-kit/Toast";
import { useState } from "react";

const showDownloadError = () =>
	toast(t("file-download-error-title"), {
		status: "error",
		description: t("file-download-error-message"),
		icon: "triangle-alert",
		size: "md",
	});

export const useDownloadAttachment = (attachment: ChatAttachment, loadedBlob?: Blob | null) => {
	const sessionId = useChatStore((s) => s.activeSessionId);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [isDownloading, setIsDownloading] = useState(false);

	const save = (blob: Blob) => downloadFile(blob, attachment.mime as MimeTypes, attachment.originalFilename);

	const download = async () => {
		if (loadedBlob) return save(loadedBlob);
		if (!sessionId || isDownloading) return;
		setIsDownloading(true);
		try {
			const blob = await fetchStoredAttachment(apiUrlCreator, sessionId, attachment);
			if (!blob) return showDownloadError();
			save(blob);
		} catch {
			showDownloadError();
		} finally {
			setIsDownloading(false);
		}
	};

	return { download, isDownloading };
};
